import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  User, Phone, MapPin, Home, IndianRupee, Calendar, Users,
  FileText, CheckCircle, ArrowRight, Search, Building2, Briefcase
} from 'lucide-react';

const API = '/api';

const PROPERTY_TYPES = ['1 BHK', '2 BHK', '3 BHK', '4+ BHK', 'Villa', 'Shop', 'Office', 'PG / Hostel', 'Warehouse'];
const FURNISHING_OPTIONS = ['Unfurnished', 'Semi-Furnished', 'Fully Furnished'];
const OCCUPANT_OPTIONS = ['Family', 'Bachelor', 'Company / Corporate'];

const TRUST_BADGES = [
  { icon: CheckCircle, label: '100% Free', color: 'text-green-600', bg: 'bg-green-50' },
  { icon: CheckCircle, label: 'Verified Platform', color: 'text-blue-600', bg: 'bg-blue-50' },
  { icon: CheckCircle, label: 'No Hidden Brokerage', color: 'text-amber-600', bg: 'bg-amber-50' },
];

const SAMPLE_REQS = [
  { property_type: '2 BHK', city: 'Vadodara', preferred_location: 'Bhayli', budget_min: 10000, budget_max: 15000, occupant_type: 'Family', name: 'Rahul S.' },
  { property_type: 'Shop', city: 'Vadodara', preferred_location: 'Alkapuri', budget_min: 25000, budget_max: 35000, occupant_type: 'Company', name: 'Priya M.' },
  { property_type: '1 BHK', city: 'Surat', preferred_location: 'Adajan', budget_min: 8000, budget_max: 12000, occupant_type: 'Bachelor', name: 'Amit K.' },
  { property_type: 'Office', city: 'Ahmedabad', preferred_location: 'SG Highway', budget_min: 30000, budget_max: 50000, occupant_type: 'Company', name: 'Neha P.' },
];

const OCCUPANT_COLOR = {
  Family: 'bg-blue-50 text-blue-700',
  Bachelor: 'bg-green-50 text-green-700',
  'Company': 'bg-purple-50 text-purple-700',
  'Company / Corporate': 'bg-purple-50 text-purple-700',
};

export default function PostRequirementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [liveReqs, setLiveReqs] = useState([]);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    city: '',
    property_type: '',
    budget_min: '',
    budget_max: '',
    preferred_location: '',
    furnishing: '',
    move_in_date: '',
    occupant_type: '',
    additional: '',
  });

  useEffect(() => {
    if (user) {
      setForm(f => ({ ...f, name: user.name || f.name, phone: user.phone || f.phone }));
    }
  }, [user]);

  useEffect(() => {
    fetch(`${API}/requirements?limit=4`)
      .then(r => r.json())
      .then(d => setLiveReqs(d.requirements || []))
      .catch(() => {});
  }, [submitted]);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.property_type) {
      toast.error('Please fill in the required fields (Name, Phone, City, Property Type)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/requirements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to post requirement');
      toast.success('Requirement posted! Owners will contact you soon.');
      setSubmitted(true);
      setForm({ name: '', phone: '', city: '', property_type: '', budget_min: '', budget_max: '', preferred_location: '', furnishing: '', move_in_date: '', occupant_type: '', additional: '' });
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const displayReqs = liveReqs.length >= 3 ? liveReqs : [...liveReqs, ...SAMPLE_REQS].slice(0, 4);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Post Rental Requirement | Rent Mitra</title>
        <meta name="description" content="Post your rental requirement and let owners & brokers contact you directly. Free, verified platform." />
        <link rel="canonical" href={`${window.location.origin}/post-requirement`} />
      </Helmet>
      <Navbar />

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-green-800 via-green-700 to-emerald-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 py-14 text-center">
          <span className="inline-flex items-center gap-2 bg-white/15 border border-white/25 text-green-100 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
            <Search className="w-3.5 h-3.5" /> For Tenants
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{fontFamily:'Outfit,sans-serif'}}>
            Post Your Rental<br />
            <span className="text-green-200">Requirement</span>
          </h1>
          <p className="text-green-100 text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Tell us what you're looking for, and let property owners &amp; brokers reach you directly.
          </p>
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-3">
            {TRUST_BADGES.map(b => (
              <div key={b.label} className="flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 text-white rounded-full px-4 py-2 text-sm font-medium">
                <b.icon className="w-4 h-4 text-green-300" />
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="max-w-4xl mx-auto px-4 -mt-6 pb-16 relative z-10">
        {submitted ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center border border-green-100">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit,sans-serif'}}>Requirement Posted!</h2>
            <p className="text-slate-500 mb-6">Owners and brokers will review your requirement and contact you directly on your mobile number.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-3 bg-green-700 hover:bg-green-800 text-white rounded-xl font-semibold transition-all active:scale-95"
              >
                Post Another Requirement
              </button>
              <button
                onClick={() => navigate('/search')}
                className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all"
              >
                Browse Properties
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-testid="requirement-form">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-green-700 to-emerald-600 px-8 py-5">
              <h2 className="text-white font-bold text-xl" style={{fontFamily:'Outfit,sans-serif'}}>
                Let Owners Contact You Directly
              </h2>
              <p className="text-green-100 text-sm mt-1">Fill the details below — it takes less than 2 minutes</p>
            </div>

            <div className="p-6 sm:p-8">
              {/* Section: Your Info */}
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-4">Your Contact Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-7">
                <InputField icon={<User className="w-4 h-4" />} label="Full Name *" placeholder="e.g. Rahul Sharma">
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => handleChange('name', e.target.value)}
                    placeholder="e.g. Rahul Sharma"
                    className="field-input"
                    data-testid="req-name"
                    required
                  />
                </InputField>
                <InputField icon={<Phone className="w-4 h-4" />} label="Mobile Number *" hint="Owners will contact you on this number">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    placeholder="e.g. 9876543210"
                    className="field-input"
                    data-testid="req-phone"
                    required
                  />
                </InputField>
              </div>

              {/* Section: Property Requirement */}
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-4">Property Requirement</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-7">
                <InputField icon={<MapPin className="w-4 h-4" />} label="City *" placeholder="e.g. Mumbai, Vadodara">
                  <input
                    type="text"
                    value={form.city}
                    onChange={e => handleChange('city', e.target.value)}
                    placeholder="e.g. Mumbai, Vadodara"
                    className="field-input"
                    data-testid="req-city"
                    required
                  />
                </InputField>
                <InputField icon={<Home className="w-4 h-4" />} label="Property Type *">
                  <select
                    value={form.property_type}
                    onChange={e => handleChange('property_type', e.target.value)}
                    className="field-input"
                    data-testid="req-property-type"
                    required
                  >
                    <option value="">Select type...</option>
                    {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </InputField>
                <InputField icon={<MapPin className="w-4 h-4" />} label="Preferred Locality" placeholder="e.g. Bhayli, Alkapuri">
                  <input
                    type="text"
                    value={form.preferred_location}
                    onChange={e => handleChange('preferred_location', e.target.value)}
                    placeholder="e.g. Bhayli, Alkapuri"
                    className="field-input"
                    data-testid="req-location"
                  />
                </InputField>
                <InputField icon={<Building2 className="w-4 h-4" />} label="Furnishing Status">
                  <select
                    value={form.furnishing}
                    onChange={e => handleChange('furnishing', e.target.value)}
                    className="field-input"
                    data-testid="req-furnishing"
                  >
                    <option value="">Any</option>
                    {FURNISHING_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </InputField>
              </div>

              {/* Section: Budget */}
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-4">Budget &amp; Timeline</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-7">
                <InputField icon={<IndianRupee className="w-4 h-4" />} label="Min Budget (₹/mo)">
                  <input
                    type="number"
                    value={form.budget_min}
                    onChange={e => handleChange('budget_min', e.target.value)}
                    placeholder="e.g. 10000"
                    className="field-input"
                    data-testid="req-budget-min"
                    min="0"
                  />
                </InputField>
                <InputField icon={<IndianRupee className="w-4 h-4" />} label="Max Budget (₹/mo)">
                  <input
                    type="number"
                    value={form.budget_max}
                    onChange={e => handleChange('budget_max', e.target.value)}
                    placeholder="e.g. 20000"
                    className="field-input"
                    data-testid="req-budget-max"
                    min="0"
                  />
                </InputField>
                <InputField icon={<Calendar className="w-4 h-4" />} label="Move-in Date">
                  <input
                    type="date"
                    value={form.move_in_date}
                    onChange={e => handleChange('move_in_date', e.target.value)}
                    className="field-input"
                    data-testid="req-move-in"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </InputField>
                <InputField icon={<Users className="w-4 h-4" />} label="Occupant Type">
                  <select
                    value={form.occupant_type}
                    onChange={e => handleChange('occupant_type', e.target.value)}
                    className="field-input"
                    data-testid="req-occupant"
                  >
                    <option value="">Select...</option>
                    {OCCUPANT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </InputField>
              </div>

              {/* Additional */}
              <p className="text-xs font-bold uppercase tracking-widest text-green-700 mb-4">Additional Details</p>
              <div className="mb-8">
                <InputField icon={<FileText className="w-4 h-4" />} label="Any specific requirements?">
                  <textarea
                    value={form.additional}
                    onChange={e => handleChange('additional', e.target.value)}
                    placeholder="e.g. Ground floor preferred, need car parking, pet-friendly..."
                    className="field-input resize-none h-24"
                    data-testid="req-additional"
                    maxLength={500}
                  />
                </InputField>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-2xl font-bold text-base text-white transition-all active:scale-[0.98] shadow-lg shadow-green-900/20 hover:shadow-xl hover:shadow-green-900/30 disabled:opacity-60 flex items-center justify-center gap-2.5"
                style={{background: 'linear-gradient(135deg, #166534, #15803d, #16a34a)'}}
                data-testid="req-submit-btn"
              >
                {submitting ? (
                  <><span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />Posting...</>
                ) : (
                  <><Briefcase className="w-5 h-5" />Post Requirement<ArrowRight className="w-4 h-4" /></>
                )}
              </button>

              <p className="text-center text-xs text-slate-400 mt-4">
                By posting, you agree that verified owners/brokers may contact you. 100% free, no charges.
              </p>
            </div>
          </form>
        )}

        {/* Requirements Board */}
        <div className="mt-14">
          <div className="text-center mb-8">
            <span className="text-xs font-bold uppercase tracking-widest text-green-700 mb-2 block">Live Board</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>
              Active Rental Requirements
            </h2>
            <p className="text-slate-500 text-sm mt-2">Owners — see what tenants are looking for</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="requirements-board">
            {displayReqs.map((req, i) => (
              <RequirementCard key={req.requirement_id || i} req={req} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .field-input {
          width: 100%;
          background: transparent;
          outline: none;
          border: none;
          font-size: 0.875rem;
          color: #1e293b;
          padding: 0;
        }
        .field-input::placeholder { color: #94a3b8; }
        .field-input option { color: #1e293b; }
      `}</style>

      {/* Sticky mobile CTA */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-slate-200 px-4 py-3 shadow-2xl">
          <button
            form="requirement-form"
            type="submit"
            onClick={() => document.querySelector('[data-testid="req-submit-btn"]')?.click()}
            className="w-full py-3.5 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 shadow-lg active:scale-95"
            style={{background: 'linear-gradient(135deg, #166534, #16a34a)'}}
          >
            <Briefcase className="w-4 h-4" />Post Requirement
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}

function InputField({ icon, label, hint, children }) {
  return (
    <div className="group">
      <label className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5">
        <span className="text-green-600">{icon}</span>
        {label}
      </label>
      <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-100 transition-all group-hover:border-slate-300">
        {children}
      </div>
      {hint && <p className="text-xs text-slate-400 mt-1 ml-1">{hint}</p>}
    </div>
  );
}

function RequirementCard({ req }) {
  const budgetStr = req.budget_min && req.budget_max
    ? `₹${Number(req.budget_min).toLocaleString('en-IN')} – ₹${Number(req.budget_max).toLocaleString('en-IN')}/mo`
    : req.budget_max
    ? `Up to ₹${Number(req.budget_max).toLocaleString('en-IN')}/mo`
    : 'Budget flexible';

  const occupantKey = Object.keys(OCCUPANT_COLOR).find(k => req.occupant_type?.includes(k)) || '';
  const tagCls = OCCUPANT_COLOR[occupantKey] || 'bg-slate-100 text-slate-600';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="requirement-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Home className="w-5 h-5 text-green-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-slate-900 text-sm leading-tight">
            Looking for {req.property_type} in {req.preferred_location || req.city}
          </h4>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" />{req.city}
          </p>
        </div>
        {req.occupant_type && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${tagCls}`}>
            {req.occupant_type.split(' ')[0]}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div>
          <span className="text-green-700 font-bold text-sm">{budgetStr}</span>
          {req.furnishing && (
            <span className="ml-2 text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{req.furnishing}</span>
          )}
        </div>
        <button
          className="text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 active:scale-95"
          onClick={() => toast.info(`Contact ${req.name || 'Tenant'} via the platform messaging system.`)}
          data-testid="req-view-details-btn"
        >
          View Details <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
